import { useQueryClient } from '@tanstack/react-query';
import { Button, Modal, TagField, TextInput } from '@yoast/ui-library';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';

import {
  getGetAnalysisListQueryKey,
  getGetAnalysisQueryKey,
  getGetAnalysisStatusQueryKey,
  getGetAssociatedBrandsQueryKey,
  useCreateBrandAssociationGroup,
} from '../../api/generated/insights';
import type { AssociatedBrandsResponseAssociatedBrandsItem } from '../../api/generated/insights.schemas';
import { useBrandAnalysisData } from '../../contexts';

type Brand = AssociatedBrandsResponseAssociatedBrandsItem;

interface GroupBrandsModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandId: string;
  brandsToGroup: Brand[];
  onGroupCreated?: () => void;
}

const GroupBrandsModal = ({
  isOpen,
  onClose,
  brandId,
  brandsToGroup,
  onGroupCreated,
}: GroupBrandsModalProps) => {
  const queryClient = useQueryClient();
  const { analysisId } = useBrandAnalysisData();

  const [groupName, setGroupName] = useState('');
  const [groupedBrands, setGroupedBrands] = useState<string[]>([]);

  // Initialize state when modal opens or brands change
  useEffect(() => {
    if (isOpen && brandsToGroup.length > 0) {
      // Set default group name to first brand's name
      const firstName = brandsToGroup[0]?.name;
      setGroupName(String(firstName || 'New Group'));

      // Set grouped brands to all provided brands
      setGroupedBrands(brandsToGroup.map((brand) => String(brand.id ?? '')));
    }
  }, [isOpen, brandsToGroup]);

  // Create group mutation
  const { mutate: createGroup, isPending: isPendingCreateGroup } =
    useCreateBrandAssociationGroup({
      mutation: {
        onSuccess: async () => {
          // Invalidate associated brands query to refresh the brand list
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: getGetAssociatedBrandsQueryKey(brandId),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisListQueryKey(brandId),
            }),
            // Grouping affects the analysis results, so invalidate those too.
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisStatusQueryKey(brandId, 'latest'),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisQueryKey(brandId, 'latest'),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisStatusQueryKey(brandId, analysisId),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisQueryKey(brandId, analysisId),
            }),
          ]);

          // Call optional callback
          onGroupCreated?.();

          // Close modal and reset state
          handleCloseModal();
        },
        onError: (error) => {
          console.error('Error creating group:', error);
        },
      },
    });

  const handleCloseModal = () => {
    setGroupName('');
    setGroupedBrands([]);
    onClose();
  };

  const handleSaveGroup = () => {
    if (!groupName.trim() || groupedBrands.length === 0) return;

    // Expand any group ids to their member ids
    const expandedBrandIds: string[] = groupedBrands.flatMap((id) => {
      const brand = brandsToGroup.find((b) => String(b.id) === id);
      if (
        brand &&
        brand.type === 'group' &&
        Array.isArray(brand.groupedBrands)
      ) {
        // Return all member ids as strings
        return brand.groupedBrands.map((b) => String(b.id));
      }
      return id;
    });

    createGroup({
      brandId,
      data: {
        groupName: groupName.trim(),
        brandIds: expandedBrandIds.filter((id) => id !== ''),
      },
    });
  };

  const handleRemoveBrand = (brandId: string) => {
    setGroupedBrands((prev) => {
      const newGroupedBrands = prev.filter((id) => id !== brandId);
      // If this was the last brand, close the modal
      if (newGroupedBrands.length === 0) {
        handleCloseModal();
      }
      return newGroupedBrands;
    });
  };

  const tags = groupedBrands
    .map((brandId) => {
      const brand = brandsToGroup.find((b) => String(b.id) === brandId);
      return brand
        ? brand.type === 'group'
          ? `Group: ${brand.name}`
          : brand.name || ''
        : '';
    })
    .filter(Boolean);

  return (
    <Modal isOpen={isOpen} onClose={handleCloseModal}>
      <Modal.Panel>
        <Modal.Container>
          <Modal.Container.Header>
            <Modal.Title>Create brand group</Modal.Title>
          </Modal.Container.Header>

          <Modal.Container.Content className="yst-space-y-4">
            {/* Group Name Field */}
            <div>
              <label
                htmlFor="groupName"
                className="yst-mb-2 yst-block yst-text-sm yst-font-medium"
              >
                Group name
              </label>
              <TextInput
                id="groupName"
                value={groupName}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setGroupName(e.target.value)
                }
                placeholder="Enter group name"
                className="yst-w-full"
              />
            </div>
            {/* Selected Brands */}
            <div>
              <TagField
                readOnly
                id="groupedBrands"
                label={`Brands in group (${groupedBrands.length})`}
                tags={tags}
                onRemoveTag={(tagToRemoveIndex: number) => {
                  // tagToRemoveIndex is the index in the tags array, use it to get the corresponding brand ID from groupedBrands
                  const brandToRemove = groupedBrands[tagToRemoveIndex];

                  if (brandToRemove) {
                    handleRemoveBrand(brandToRemove);
                  }
                }}
                onAddTag={() => {
                  // Not needed for this use case
                }}
              />
              {groupedBrands.length === 0 && (
                <p className="yst-text-sm yst-italic yst-text-gray-500">
                  No brands selected
                </p>
              )}
            </div>
            <p>
              Note: To add brands to this group, save this group, then use the
              checkboxes to select this group as well as any additional brands
              to be grouped.
            </p>
          </Modal.Container.Content>

          <Modal.Container.Footer className="yst-pt-4">
            <div className="yst-flex yst-justify-end yst-gap-2">
              <Button
                variant="secondary"
                onClick={handleCloseModal}
                disabled={isPendingCreateGroup}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveGroup}
                disabled={
                  !groupName.trim() ||
                  groupedBrands.length === 0 ||
                  isPendingCreateGroup
                }
              >
                {isPendingCreateGroup ? 'Saving...' : 'Save group'}
              </Button>
            </div>
          </Modal.Container.Footer>
        </Modal.Container>
      </Modal.Panel>
    </Modal>
  );
};

export default GroupBrandsModal;
